/**
 * Sign-in shell. Three flows, each config-driven off /auth/config so the button is
 * live only when the server actually offers it (fail-safe), per TRE-786:
 *   - Azure AD SSO (button → /auth/login?redirect_uri=<this origin>), shown when
 *     `ssoEnabled`. Works on the hosted web admin now that TRE-833 allows the
 *     configured admin web origin as a redirect target; the minted session comes
 *     back on `?session=` (captured in App on load). Needs ADMIN_WEB_ORIGIN on host.
 *   - Google SSO (button → /auth/google/login?redirect_uri=<this origin>), shown
 *     when `googleEnabled`. The IdP backend landed (TRE-830) and TRE-838 gave the
 *     Google login route the same admin-web-origin redirect allow-list as Azure, so
 *     the web round-trip completes. `googleEnabled` stays false until an OAuth client
 *     is registered, so the button self-disables until go-live — no dead button.
 *   - Email one-time code (/auth/email/start → /auth/email/verify) — always works
 *     for a hosted web origin; verify returns the session inline.
 */

import { useState } from "react";
import { api, ApiError } from "../api/client";
import { clearInviteParam, getInviteToken, setToken } from "../auth/session";
import { useAsyncData } from "../lib/useAsyncData";

const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/$/, "");

export function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const cfg = useAsyncData(() => api.authConfig(), []);
  // An invite link (TRE-1044) opens the SPA with `?invite=<token>`; carry it into
  // whichever sign-in flow the invitee picks so they're admitted + bound in one go.
  const [invite] = useState(() => getInviteToken());
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState<"email" | "code">("email");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function startEmail(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.emailStart(email.trim());
      setStage("code");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't send the code.");
    } finally {
      setBusy(false);
    }
  }

  async function verify(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.emailVerify(email.trim(), code.trim(), invite ?? undefined);
      setToken(res.session);
      clearInviteParam();
      onSignedIn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "That code didn't verify.");
    } finally {
      setBusy(false);
    }
  }

  const ssoEnabled = cfg.value?.ssoEnabled ?? false;
  const googleEnabled = cfg.value?.googleEnabled ?? false;

  // Start a browser SSO round-trip: the server redirects to the IdP and, on
  // callback, hands the session back to this origin on `?session=` (TRE-833/838).
  const startSso = (loginPath: string) => {
    const redirect = window.location.origin + window.location.pathname;
    const inviteParam = invite ? `&invite=${encodeURIComponent(invite)}` : "";
    window.location.href = `${API_BASE}${loginPath}?redirect_uri=${encodeURIComponent(redirect)}${inviteParam}`;
  };

  return (
    <div className="center-pane">
      <div className="card">
        <span className="brand">Treillage Admin</span>
        {invite ? (
          <div className="banner banner--info" role="status">
            You've been invited to a Treillage account. Sign in with any method below to accept.
          </div>
        ) : (
          <p className="screen-intro">Sign in to manage your account's users, skills, and billing.</p>
        )}

        {error && (
          <div className="banner banner--error" role="alert">
            {error}
          </div>
        )}

        <div className="stack">
          {/* Industry-standard provider buttons (per Google / Microsoft brand
              guidelines). Only rendered for providers the server actually offers
              (from /auth/config) — no dead, greyed-out buttons. */}
          {googleEnabled && (
            <button
              type="button"
              className="sso-btn sso-btn--google"
              onClick={() => startSso("/auth/google/login")}
            >
              <GoogleG />
              <span>Sign in with Google</span>
            </button>
          )}
          {ssoEnabled && (
            <button
              type="button"
              className="sso-btn sso-btn--ms"
              onClick={() => startSso("/auth/login")}
            >
              <MicrosoftLogo />
              <span>Sign in with Microsoft</span>
            </button>
          )}
          {!googleEnabled && !ssoEnabled && (
            <p className="muted sso-empty">
              Single sign-on isn't configured on this server yet — use an email code below.
            </p>
          )}
        </div>

        <div className="hr-or">or use an email code</div>

        {stage === "email" ? (
          <form onSubmit={startEmail}>
            <label className="field">
              <span>Work email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="you@firm.com"
              />
            </label>
            <button type="submit" className="btn btn--primary" disabled={busy || !email.trim()}>
              {busy ? "Sending…" : "Send code"}
            </button>
          </form>
        ) : (
          <form onSubmit={verify}>
            <p className="muted">
              We sent a one-time code to <strong>{email}</strong>.
            </p>
            <label className="field">
              <span>One-time code</span>
              <input
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                autoComplete="one-time-code"
                autoFocus
              />
            </label>
            <div className="row-actions" style={{ justifyContent: "space-between" }}>
              <button type="button" className="btn--link" onClick={() => setStage("email")} disabled={busy}>
                ← Use a different email
              </button>
              <button type="submit" className="btn btn--primary" disabled={busy || !code.trim()}>
                {busy ? "Verifying…" : "Verify & sign in"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/** Official Google "G" mark (4-color), per Google Identity branding. */
function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.71-1.57 2.68-3.89 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.85.86-3.04.86-2.34 0-4.32-1.58-5.03-3.71H.96v2.33A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.97 10.71a5.41 5.41 0 0 1 0-3.42V4.96H.96a9 9 0 0 0 0 8.08l3.01-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}

/** Official Microsoft four-square mark, per Microsoft brand guidelines. */
function MicrosoftLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <rect x="0" y="0" width="8" height="8" fill="#F25022" />
      <rect x="10" y="0" width="8" height="8" fill="#7FBA00" />
      <rect x="0" y="10" width="8" height="8" fill="#00A4EF" />
      <rect x="10" y="10" width="8" height="8" fill="#FFB900" />
    </svg>
  );
}
